/**
 * ArubaRdvPage — Reception Device Validation queue.
 *
 * Devices arrive at the warehouse and land in the RDV queue. This page lets
 * the warehouse operator see what's queued, re-trigger validation for failed
 * items, and approve a device into a target site once validation passes.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  PlayCircle,
  RotateCw,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  TableShell,
  TableHeadRow,
  TableRow,
  TableHeadCell,
  TableCell,
} from "@/components/ui/table-shell";
import { TableSkeleton } from "@/components/ui/skeleton";
import { ArubaSubNav } from "@/components/app/aruba-sub-nav";
import { NoVendorKeysBanner } from "@/components/app/no-vendor-keys-banner";
import { useArubaRdv } from "@/stores/aruba-rdv";
import type { ArubaRdvDevice } from "@/api/schemas/aruba";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";

const COLS = "1.1fr 0.7fr 0.9fr 1fr 1.2fr 1.4fr";

function stateBadge(state: ArubaRdvDevice["state"]) {
  switch (state) {
    case "ok":
      return (
        <Badge variant="ok" dot>
          validated
        </Badge>
      );
    case "validating":
      return (
        <Badge variant="info" dot>
          validating
        </Badge>
      );
    case "failed":
      return (
        <Badge variant="err" dot>
          failed
        </Badge>
      );
    default:
      return (
        <Badge variant="warn" dot>
          queued
        </Badge>
      );
  }
}
function stateIcon(state: ArubaRdvDevice["state"]) {
  switch (state) {
    case "ok":
      return <CheckCircle2 size={14} className="text-mint" />;
    case "validating":
      return <Clock size={14} className="text-violet" />;
    case "failed":
      return <XCircle size={14} className="text-rose" />;
    default:
      return <Clock size={14} className="text-peach" />;
  }
}

export function ArubaRdvPage() {
  const animatedRootRef = useUiAutoAnimate();

  const { data, status, error, load, rerun, approve } = useArubaRdv();
  useEffect(() => {
    void load();
  }, [load]);

  // Approval inline state — which serial is open + the target site value.
  const [approvingSerial, setApprovingSerial] = useState<string | null>(null);
  const [approvalSite, setApprovalSite] = useState("");

  const [filter, setFilter] = useState<"all" | ArubaRdvDevice["state"]>("all");

  const visible = useMemo(() => {
    if (filter === "all") return data;
    return data.filter((d) => d.state === filter);
  }, [data, filter]);

  const counts = useMemo(() => {
    const c = { all: data.length, queued: 0, validating: 0, ok: 0, failed: 0 };
    for (const d of data) c[d.state]++;
    return c;
  }, [data]);

  function startApproval(serial: string, prefilled?: string) {
    setApprovingSerial(serial);
    setApprovalSite(prefilled ?? "");
  }

  async function confirmApproval() {
    if (!approvingSerial) return;
    if (!approvalSite.trim()) {
      toast.info("Pick a target site");
      return;
    }
    await approve(approvingSerial, approvalSite.trim());
    toast.success(`Approved into ${approvalSite.trim()}`);
    setApprovingSerial(null);
    setApprovalSite("");
  }

  return (
    <div ref={animatedRootRef} className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">
          Operations
        </Link>
        <span className="opacity-40">/</span>
        <Link to="/aruba/keys" className="text-fg-dim hover:text-fg">
          Aruba
        </Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">RDV queue</span>
      </div>

      <ArubaSubNav />

      <NoVendorKeysBanner vendor="aruba" />

      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">
            RDV queue
          </h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            Reception Device Validation — devices received at the warehouse
            waiting to be validated and approved into a target site. Failed
            items can be retried; validated items can be approved here.
          </p>
        </div>
        <Badge variant={counts.failed > 0 ? "err" : "info"} size="md">
          {counts.failed > 0 ? (
            <AlertTriangle size={11} />
          ) : (
            <CheckCircle2 size={11} />
          )}
          {counts.failed > 0 ? `${counts.failed} failed` : "no failures"}
        </Badge>
      </div>

      {error ? (
        <output
          className="flex items-center gap-2.5 rounded-xl border border-peach/30 bg-peach/10 px-3.5 py-2.5 text-sm text-peach"
        >
          <AlertTriangle size={16} />
          <span>
            <b className="font-semibold">Showing fixture data.</b>{" "}
            <span className="text-peach/80">{error}</span>
          </span>
        </output>
      ) : null}

      {/* State counters + filter tabs */}
      <Card>
        <CardContent className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 text-xs text-fg-dim">
            <span>
              <b className="font-mono text-fg">{counts.all}</b> total
            </span>
            <span>·</span>
            <span className="text-peach">
              <b className="font-mono">{counts.queued}</b> queued
            </span>
            <span className="text-violet">
              <b className="font-mono">{counts.validating}</b> validating
            </span>
            <span className="text-mint">
              <b className="font-mono">{counts.ok}</b> validated
            </span>
            <span className="text-rose">
              <b className="font-mono">{counts.failed}</b> failed
            </span>
          </div>
          <Tabs
            value={filter}
            onValueChange={(v) => setFilter(v as typeof filter)}
          >
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="queued">Queued</TabsTrigger>
              <TabsTrigger value="validating">Validating</TabsTrigger>
              <TabsTrigger value="ok">Validated</TabsTrigger>
              <TabsTrigger value="failed">Failed</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardContent>
      </Card>

      {status === "loading" && data.length === 0 ? (
        <TableSkeleton columns={COLS} columnCount={6} rows={6} />
      ) : visible.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <CardTitle>No devices in this state</CardTitle>
            <CardSub>Try a different filter, or wait for a new batch.</CardSub>
          </CardContent>
        </Card>
      ) : (
        <TableShell>
          <TableHeadRow columns={COLS}>
            <TableHeadCell>Serial</TableHeadCell>
            <TableHeadCell>Model</TableHeadCell>
            <TableHeadCell>State</TableHeadCell>
            <TableHeadCell>Received</TableHeadCell>
            <TableHeadCell>Target site</TableHeadCell>
            <TableHeadCell />
          </TableHeadRow>
          {visible.map((d) => {
            const isOpen = approvingSerial === d.serial;
            return (
              <TableRow
                key={d.serial}
                columns={COLS}
                className={isOpen ? "bg-aurora-soft/30" : ""}
              >
                <TableCell className="font-mono text-[11px] text-fg">
                  {d.serial}
                </TableCell>
                <TableCell className="font-mono text-[10.5px] text-fg-dim">
                  {d.model}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {stateIcon(d.state)}
                    {stateBadge(d.state)}
                  </div>
                  {d.note ? (
                    <div className="mt-1 text-[10.5px] text-fg-mute">
                      {d.note}
                    </div>
                  ) : null}
                </TableCell>
                <TableCell>
                  <div className="text-[11px] text-fg">
                    {new Date(d.receivedAt).toLocaleDateString()}
                  </div>
                  <div className="text-[10.5px] text-fg-mute">
                    by {d.receivedBy}
                  </div>
                </TableCell>
                <TableCell>
                  {isOpen ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={approvalSite}
                        onChange={(e) => setApprovalSite(e.target.value)}
                        placeholder="e.g. NYC-21"
                        className="h-8 px-2 text-xs"
                      />
                      <Button size="sm" onClick={() => void confirmApproval()}>
                        <CheckCircle2 size={13} />
                        OK
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setApprovingSerial(null)}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <span className="text-xs text-fg-dim">
                      {d.targetSite ?? "—"}
                    </span>
                  )}
                </TableCell>
                <TableCell className="flex justify-end gap-2">
                  {d.state === "queued" || d.state === "failed" ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => void rerun(d.serial)}
                    >
                      <RotateCw size={13} />
                      Re-run
                    </Button>
                  ) : null}
                  {d.state === "ok" && !isOpen ? (
                    <Button
                      size="sm"
                      onClick={() =>
                        startApproval(d.serial, d.targetSite ?? "")
                      }
                    >
                      <PlayCircle size={13} />
                      Approve
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            );
          })}
        </TableShell>
      )}
    </div>
  );
}
